import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  PROGRESS_PHOTO_BUCKET, latestPhotoSet, noPhotosSentence, viewLabel,
  type MeasurementPhotoRow, type PhotoSet, type PhotoView,
} from "../lib/progressPhotos";
import { dayKey } from "../lib/days";
import { shortDate } from "../lib/weekLine";

/**
 * `Progress Photo` — the Overview's fourth narrow card (`EVERFIT-MAP` §2-bis column 2). The rules
 * live in `lib/progressPhotos.ts` with their reasons; this file fetches and draws.
 *
 * ⚠️ THREE STATES, AND THEY ARE THREE: loading, a refused/failed read (the door's own words), and
 * the answer — a set of photos or their empty sentence. "Could not read" is never drawn as "hasn't
 * uploaded any", which would tell a coach something false about their client.
 */
export function ProgressPhotoCard({ clientId, firstName }: { clientId: string; firstName: string | null }) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; set: PhotoSet | null }
  >({ kind: "loading" });
  const [urls, setUrls] = useState<Partial<Record<PhotoView, string>>>({});

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      const { data, error } = await supabase
        .from("body_measurements")
        .select("measured_at, photo_front_path, photo_side_path, photo_back_path")
        .eq("user_id", clientId)
        .or("photo_front_path.not.is.null,photo_side_path.not.is.null,photo_back_path.not.is.null")
        .order("measured_at", { ascending: false })
        .limit(5);
      if (cancelled) return;
      if (error !== null) { setState({ kind: "error", message: `Photos could not be read: ${error.message}` }); return; }
      setState({ kind: "ready", set: latestPhotoSet((data ?? []) as MeasurementPhotoRow[]) });
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  /* The images, fetched with the coach's own token — see the module header on why a download and
     never a signed URL. Each object URL is REVOKED when the set changes or the card unmounts, or a
     coach paging through clients would leave every photo they ever opened in the tab's memory. */
  const set = state.kind === "ready" ? state.set : null;
  useEffect(() => {
    if (set === null) return;
    let cancelled = false;
    const made: string[] = [];
    (async () => {
      for (const p of set.photos) {
        const { data } = await supabase.storage.from(PROGRESS_PHOTO_BUCKET).download(p.path);
        if (cancelled || data === null) continue;
        const url = URL.createObjectURL(data);
        made.push(url);
        setUrls((u) => ({ ...u, [p.view]: url }));
      }
    })();
    return () => {
      cancelled = true;
      made.forEach((u) => URL.revokeObjectURL(u));
      setUrls({});
    };
  }, [set]);

  /* THE PAGE'S ONE DAY CONVENTION — `dayKey`, which is UTC on purpose (its reasons and its stated
     cost are in `lib/days.ts`). The weight beside this card counts its days the same way, so the
     photo and the number taken with it can never be printed as two different days. */
  const whenKey = set === null ? null : dayKey(set.measuredAtISO);
  const when = whenKey === null ? "" : shortDate(whenKey);

  return (
    <section aria-labelledby="progress-photo-h" className="client-photos">
      <h2 id="progress-photo-h" className="ht-overline client-section-heading">Progress photo</h2>
      {state.kind === "loading" && <p className="ht-caption client-metrics-empty">Loading photos…</p>}
      {state.kind === "error" && <p className="ht-caption client-metrics-empty" role="alert">{state.message}</p>}
      {state.kind === "ready" && state.set === null && (
        <p className="ht-caption client-metrics-empty">{noPhotosSentence(firstName)}</p>
      )}
      {set !== null && (
        <>
          <ul className="client-photos-row">
            {set.photos.map((p) => (
              <li key={p.view} className="client-photo">
                {urls[p.view] === undefined ? (
                  <span className="client-photo-frame is-loading" aria-hidden="true" />
                ) : (
                  <img className="client-photo-frame" src={urls[p.view]}
                       alt={`${viewLabel(p.view)} progress photo, ${when}`} />
                )}
                <span className="ht-caption client-photo-view">{viewLabel(p.view)}</span>
              </li>
            ))}
          </ul>
          <p className="ht-caption client-photo-when">{when}</p>
        </>
      )}
    </section>
  );
}
